import { RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, NetworkAction, action, entityActionSet, networkAction } from './utils';
import { download } from 'typed/utils';

export const
    { loadAll: loadAllForesightScenarioBaselineCalculations } = entityActionSet(defaults.ForesightScenarioBaselineCalculation, {
        loadAll: networkAction('LOAD_ALL_FORESIGHT_SCENARIO_BASELINE_CALCULATIONS', getAPIMethod('ForesightScenarioBaselineCalculation', 'loadAll'))
    }),

    foresightScenarios = {
        ...entityActionSet(defaults.ForesightScenarioReport, {
            loadAll: networkAction('LOAD_ALL_FORESIGHT_SCENARIOS', getAPIMethod('ForesightScenario', 'loadAll')),
            load: networkAction('LOAD_FORESIGHT_SCENARIO', getAPIMethod('ForesightScenario', 'load')),
            add: networkAction('ADD_FORESIGHT_SCENARIO', getAPIMethod('ForesightScenario', 'add')),
            edit: networkAction('EDIT_FORESIGHT_SCENARIO', getAPIMethod('ForesightScenario', 'edit')),
            delete: networkAction('DELETE_FORESIGHT_SCENARIO', getAPIMethod('ForesightScenario', 'delete')),
            voidAll: action('VOID_ALL_FORESIGHT_SCENARIOS')
        }),
        edit: (entity: Partial<entities.ForesightScenarioReport> & { ID: number }, sync = true, reloadAfterSync = false):
            NetworkAction<'EDIT_FORESIGHT_SCENARIO', { entityID: number; entity: Partial<entities.ForesightScenarioReport>; }, void> =>
            async (dispatch: any) => {
                dispatch(action('EDIT_FORESIGHT_SCENARIO', { entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }));

                if (!sync) {
                    return null;
                }
                else {
                    await dispatch(networkAction('EDIT_FORESIGHT_SCENARIO', { entityID: entity.ID, entity }, getAPIMethod('ForesightScenario', 'edit'), entity));

                    if (reloadAfterSync) {
                        dispatch(foresightScenarios.load(entity.ID));
                    }

                    return;
                }
            },

        add: (entity: entities.ForesightScenarioReport, sync = false, reloadAfterSync = false):
        NetworkAction<'ADD_FORESIGHT_SCENARIO', {entityID: number; entity: entities.ForesightScenarioReport;}, number> =>
            async(dispatch: any) => {
                dispatch(action('ADD_FORESIGHT_SCENARIO', { entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }));

                if (!sync) {
                    return null;
                }
                else {
                    const newID = await dispatch(networkAction('ADD_FORESIGHT_SCENARIO', { entityID: entity.ID, entity }, getAPIMethod('ForesightScenario', 'add'), entity));

                    if (reloadAfterSync) {
                        dispatch(foresightScenarios.load(newID!));
                    }

                    return newID;
                }
            },
        exportData: (entityID: number, filename = ''): NetworkAction<'EXPORT_FORESIGHT_SCENARIO_DATA', { entityID: number; filename: string; }, void> =>
            async(dispatch) => {
                const templateURL = await dispatch(networkAction(
                    'EXPORT_FORESIGHT_SCENARIO_DATA',
                    { entityID, filename },
                    getAPIMethod<string>('ForesightScenario', 'exportData'),
                    entityID
                ));
                if (templateURL) {
                    download(templateURL, filename);
                }
            },
        calculateCashflow: (entityID: number): NetworkAction<'CALCULATE_FORESIGHT_SCENARIO_CASHFLOW', {entityID: number;}, void> =>
            networkAction('CALCULATE_FORESIGHT_SCENARIO_CASHFLOW', { entityID }, getAPIMethod('ForesightScenario', 'calculateCashflow'), entityID),
        calculateForecast: (entityID: number): NetworkAction<'CALCULATE_FORESIGHT_SCENARIO_FORECAST', {entityID: number;}, void> =>
            networkAction('CALCULATE_FORESIGHT_SCENARIO_FORECAST', { entityID }, getAPIMethod('ForesightScenario', 'calculateData'), entityID),
        cloneScenario: (entityID: number, name: string): NetworkAction<'CLONE_FORESIGHT_SCENARIO', {entityID: number;}, entities.ForesightScenarioReport> =>
            networkAction('CLONE_FORESIGHT_SCENARIO', { entityID, name }, getAPIMethod('ForesightScenario', 'cloneScenario'), entityID, name)
    },

    foresightScenarioElementTypes = entityActionSet(defaults.ForesightScenarioReport, defaults.ForesightScenarioElementType, {
        addAll: networkAction('ADD_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES', getAPIMethod('ForesightScenarioElementType', 'addAll')),
        editAll: networkAction('EDIT_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES', getAPIMethod('ForesightScenarioElementType', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_FORESIGHT_SCENARIO_ELEMENT_TYPES', getAPIMethod('ForesightScenarioElementType', 'deleteAll'))
    }),

    cashflowAccountReceivablesDistributions = entityActionSet(defaults.ForesightScenarioReport, defaults.BusinessSimulatorCashflowAccountReceivablesDistribution, {
        addAll: networkAction('ADD_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowAccountReceivablesDistribution', 'addAll')),
        editAll: networkAction('EDIT_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowAccountReceivablesDistribution', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_BUSINESS_SIMULATOR_ACCOUNT_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowAccountReceivablesDistribution', 'deleteAll'))
    }),

    cashflowSalesReceivablesDistributions = entityActionSet(defaults.ForesightScenarioReport, defaults.BusinessSimulatorCashflowSalesReceivablesDistribution, {
        addAll: networkAction('ADD_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowSalesReceivablesDistribution', 'addAll')),
        editAll: networkAction('EDIT_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowSalesReceivablesDistribution', 'editAll')),
        deleteAll: networkAction('DELETE_ALL_BUSINESS_SIMULATOR_SALES_RECEIVABLES_DISTRIBUTIONS', getAPIMethod('BusinessSimulatorCashflowSalesReceivablesDistribution', 'deleteAll'))
    }),

    { loadAll: loadAllCashflowCapitalItemGroups } = entityActionSet(defaults.BusinessSimulatorCashflowCapitalItemGroup, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_SIMULATOR_CASHFLOW_CAPITAL_ITEM_GROUPS', getAPIMethod('BusinessSimulatorCashflowCapitalItemGroup', 'loadAll'))
    }),

    { loadAll: loadAllCashflowCapitalItems } = entityActionSet(defaults.BusinessSimulatorCashflowCapitalItem, {
        loadAll: networkAction('LOAD_ALL_BUSINESS_SIMULATOR_CASHFLOW_CAPITAL_ITEMS', getAPIMethod('BusinessSimulatorCashflowCapitalItem', 'loadAll'))
    }),

    cashflowCapitalItemValues = {
        loadSlice: (parentID: number, options: { startID?: number; maxCount?: number; slicingOrder?: number;} = {}) =>
            networkAction(
                'LOAD_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES_SLICE',
                { parentID, template: { ScenarioID: parentID } },
                getAPIMethod<entities.NetworkPaginationSlice<entities.BusinessSimulatorCashflowCapitalItemValue>>(
                    'BusinessSimulatorCashflowCapitalItemValue',
                    'searchSlice'
                ),
                { ScenarioID: parentID },
                options
            ),
        ...entityActionSet(defaults.ForesightScenarioReport, defaults.BusinessSimulatorCashflowCapitalItemValue, {
            addAll: networkAction('ADD_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES', getAPIMethod('BusinessSimulatorCashflowCapitalItemValue', 'addAll')),
            editAll: networkAction('EDIT_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES', getAPIMethod('BusinessSimulatorCashflowCapitalItemValue', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_BUSINESS_SIMULATOR_CAPITAL_ITEM_VALUES', getAPIMethod('BusinessSimulatorCashflowCapitalItemValue', 'deleteAll'))
        }),
        copyFromScenario: (sourceScenarioID: number, targetScenarioID: number) =>
            networkAction(
                'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO',
                { entityID: targetScenarioID, sourceScenarioID, targetScenarioID },
                getAPIMethod<void>('BusinessSimulatorCashflowCapitalItemValue', 'copyCapitalItemValuesFromScenario'),
                sourceScenarioID,
                targetScenarioID
            )
    },

    foresightScenarioLevels = {
        ...entityActionSet(defaults.ForesightScenarioReport, defaults.ForesightScenarioLevelReport, {
            load: networkAction('LOAD_FORESIGHT_SCENARIO_LEVEL', getAPIMethod('ForesightScenarioLevel', 'load')),
            add: networkAction('ADD_FORESIGHT_SCENARIO_LEVEL', getAPIMethod('ForesightScenarioLevel', 'add')),
            editAll: networkAction('EDIT_ALL_FORESIGHT_SCENARIO_LEVELS', getAPIMethod('ForesightScenarioLevel', 'editAll')),
            edit: networkAction('EDIT_FORESIGHT_SCENARIO_LEVEL', getAPIMethod('ForesightScenarioLevel', 'edit')),
            deleteAll: networkAction('DELETE_ALL_FORESIGHT_SCENARIO_LEVELS', getAPIMethod('ForesightScenarioLevel', 'deleteAll')),
            delete: networkAction('DELETE_FORESIGHT_SCENARIO_LEVEL', getAPIMethod('ForesightScenarioLevel', 'delete'))
        }),
        loadMany: (parentID: number, entityIDs: number[]) =>
            networkAction(
                'LOAD_MANY_FORESIGHT_SCENARIO_LEVELS',
                { parentID, entityIDs },
                getAPIMethod<entities.ForesightScenarioLevelReport[]>('ForesightScenarioLevel', 'loadMany'),
                entityIDs
            ),
        addAll: (parentID: number, entities: entities.ForesightScenarioLevelReport[], sync = false, reloadAfterSync = false):
        NetworkAction<'ADD_ALL_FORESIGHT_SCENARIO_LEVELS', { parentID: number; entities: entities.ForesightScenarioLevelReport[]; }, number> =>
            async(dispatch: any) => {
                dispatch(action('ADD_ALL_FORESIGHT_SCENARIO_LEVELS', { parentID, entities, entityIDs: entities.map(({ ID }) => ID), status: RequestStatus.UNSYNCED }));

                if (!sync) {
                    return null;
                }
                else {
                    const newIDs = await dispatch(networkAction(
                        'ADD_ALL_FORESIGHT_SCENARIO_LEVELS',
                        { parentID, entities, entityIDs: entities.map(({ ID }) => ID) },
                        getAPIMethod('ForesightScenarioLevel', 'addAll'),
                        entities
                    ));

                    if (newIDs?.length && reloadAfterSync) {
                        dispatch(foresightScenarioLevels.loadMany(parentID, newIDs!));
                    }

                    return newIDs;
                }
            },
        addWithTrackingLevelItem: (parentID: number, entity: entities.ForesightScenarioLevelReport, itemName: string):
        NetworkAction<'ADD_FORESIGHT_SCENARIO_LEVEL_WITH_TRACKING_LEVEL_ITEM', { parentID: number; entity: entities.ForesightScenarioLevelReport; itemName: string; }, number> =>
            async(dispatch: any) => {
                const response = await dispatch(networkAction(
                    'ADD_FORESIGHT_SCENARIO_LEVEL_WITH_TRACKING_LEVEL_ITEM',
                    { parentID, entity, itemName },
                    getAPIMethod('ForesightScenarioLevel', 'addWithTrackingLevelItem'),
                    entity,
                    itemName
                ));

                if (response) {
                    dispatch(foresightScenarioLevels.load(parentID, response));
                }

                return response;
            }
    },

    foresightScenarioElements = {
        ...entityActionSet(defaults.ForesightScenarioReport, defaults.ForesightScenarioLevelReport, defaults.ForesightScenarioElementReport, {
            load: networkAction('LOAD_FORESIGHT_SCENARIO_ELEMENT', getAPIMethod('ForesightScenarioElement', 'load')),
            add: networkAction('ADD_FORESIGHT_SCENARIO_ELEMENT', getAPIMethod('ForesightScenarioElement', 'add')),
            edit: networkAction('EDIT_FORESIGHT_SCENARIO_ELEMENT', getAPIMethod('ForesightScenarioElement', 'edit')),
            delete: networkAction('DELETE_FORESIGHT_SCENARIO_ELEMENT', getAPIMethod('ForesightScenarioElement', 'delete'))
        }),
        copyBudgetFromSourceIndicator: (grandParentID: number, parentID: number, entityID: number) =>
            networkAction(
                'COPY_BUDGET_FROM_FORESIGHT_SCENARIO_ELEMENT_SOURCE_INDICATOR',
                { grandParentID, parentID, entityID },
                getAPIMethod('ForesightScenarioElement', 'copyBudgetFromSourceIndicator'),
                entityID
            )
    },

    foresightScenarioFactors = {
        ...entityActionSet(defaults.ForesightScenarioReport, defaults.ForesightScenarioLevelReport, defaults.ForesightScenarioFactor, {
            addAll: networkAction('ADD_ALL_FORESIGHT_SCENARIO_FACTORS', getAPIMethod('ForesightScenarioFactor', 'addAll')),
            deleteAll: networkAction('DELETE_ALL_FORESIGHT_SCENARIO_FACTORS', getAPIMethod('ForesightScenarioFactor', 'deleteAll'))
        }),
        ...entityActionSet(defaults.ForesightScenarioReport, defaults.ForesightScenarioLevelReport, defaults.ForesightScenarioElementReport, defaults.ForesightScenarioFactor, {
            load: networkAction('LOAD_FORESIGHT_SCENARIO_FACTOR', getAPIMethod('ForesightScenarioFactor', 'load')),
            add: networkAction('ADD_FORESIGHT_SCENARIO_FACTOR', getAPIMethod('ForesightScenarioFactor', 'add')),
            edit: networkAction('EDIT_FORESIGHT_SCENARIO_FACTOR', getAPIMethod('ForesightScenarioFactor', 'edit')),
            delete: networkAction('DELETE_FORESIGHT_SCENARIO_FACTOR', getAPIMethod('ForesightScenarioFactor', 'delete'))
        })
    };

export type ForesightsAction = ExtractAction<
| typeof loadAllForesightScenarioBaselineCalculations
| typeof foresightScenarios
| typeof foresightScenarioElementTypes
| typeof foresightScenarioLevels
| typeof foresightScenarioElements
| typeof foresightScenarioFactors
| typeof cashflowAccountReceivablesDistributions
| typeof cashflowSalesReceivablesDistributions
| typeof loadAllCashflowCapitalItemGroups
| typeof loadAllCashflowCapitalItems
| typeof cashflowCapitalItemValues
>;
